import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Box,
  Text,
  Heading,
  VStack,
  Tooltip,
  HStack,
} from "@chakra-ui/react";
import { QuestionIcon } from "@chakra-ui/icons";
import { format } from "date-fns";
import { ScheduledMessage } from "../../api/clientUser/getCampaignScheduledMessages";

interface Props {
  messages: ScheduledMessage[];
}

const getStatusColor = (status: string) => {
  switch (status) {
    case "PENDING":
      return "blue";
    case "SENDING":
      return "yellow";
    case "SENT":
      return "green";
    case "FAILED":
      return "red";
    default:
      return "gray";
  }
};

export const ScheduledMessagesTable = ({ messages }: Props) => {
  if (messages.length === 0) {
    return (
      <VStack spacing={4} align="center" py={8}>
        <Text fontSize="lg" color="gray.500">
          No scheduled messages for this campaign
        </Text>
      </VStack>
    );
  }

  return (
    <VStack align="stretch" spacing={4} width="100%">
      <Heading size="md" color="#1A202C" fontWeight="700">
        Scheduled Messages
      </Heading>

      <Box
        borderWidth="1px"
        borderRadius="lg"
        overflow="hidden"
        bg="white"
        shadow="sm"
      >
        <Table variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th>Message</Th>
              <Th>Scheduled</Th>
              <Th>Status</Th>
              <Th isNumeric>Recipients</Th>
              <Th isNumeric>Delivered</Th>
              <Th isNumeric>Failed</Th>
              <Th isNumeric>
                <HStack spacing={1} justify="flex-end">
                  <Text>Skipped</Text>
                  <Tooltip
                    label="Users who have already donated to this campaign do not receive subsequent messages"
                    placement="top"
                    hasArrow
                  >
                    <QuestionIcon boxSize={3} color="gray.500" />
                  </Tooltip>
                </HStack>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {messages.map((msg) => (
              <Tr key={msg.id}>
                <Td maxW="300px">
                  <Text noOfLines={2} fontSize="sm">
                    {msg.messageText}
                  </Text>
                </Td>
                <Td>
                  <Text fontSize="sm">
                    {format(new Date(msg.scheduledFor), "MMM d, yyyy")}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {format(new Date(msg.scheduledFor), "h:mm a")}
                  </Text>
                </Td>
                <Td>
                  <Badge colorScheme={getStatusColor(msg.status)} fontSize="xs">
                    {msg.status}
                  </Badge>
                </Td>
                <Td isNumeric>
                  <Text fontSize="sm">
                    {msg.totalRecipients ?? "-"}
                  </Text>
                </Td>
                <Td isNumeric>
                  <Text fontSize="sm" color="green.600" fontWeight="600">
                    {msg.deliveredCount ?? "-"}
                  </Text>
                </Td>
                <Td isNumeric>
                  <Text fontSize="sm" color="red.600" fontWeight="600">
                    {msg.failedCount ?? "-"}
                  </Text>
                </Td>
                <Td isNumeric>
                  <Text fontSize="sm" color="orange.600" fontWeight="600">
                    {msg.skippedCount ?? "-"}
                  </Text>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </VStack>
  );
};
